import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { authClient } from "../lib/authApi";
import { getSettings } from "../lib/authApi";
import { listIngestRuns, calculate as calcApi, listClients } from "../lib/api";
import { RerunWarningDialog } from "./RerunWarningDialog";
import { buildCalcPayload } from "../lib/calcPayload";
import { normalizePeriodFxRates } from "../lib/periodFxRates";
import { errorMessage } from "../lib/errorMessage";
import { deriveWorkflowState } from "./workflowState";
import { stepPath, parseStepFromPath } from "./workflowRoutes";
import { resolveBootCalcIds } from "./bootCalcId";
import { useWorkflowRestore } from "./useWorkflowRestore";
import { useCalcStatusRefresh } from "./useCalcStatusRefresh";

/**
 * v0.4.20 UI8 — WorkflowProvider. Hosts the canonical state for the
 * 6-step filing workflow. Components subscribe via useWorkflow().
 *
 * State sources:
 *   - selectedRunIds, periodStart, periodEnd, countryFilter (local)
 *   - registrations (from /api/settings, ADMIN only)
 *   - calc           (the calc result if any)
 *   - review         (from /api/runs/calc/{id}/review-grouped)
 *   - acknowledgements (from /api/runs/calc/{id}/acknowledgements)
 * Derived: deriveWorkflowState() in workflowState.js.
 */

const WorkflowCtx = createContext(null);

export const WorkflowProvider = ({ children }) => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [runs, setRuns] = useState([]);
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [periodStart, setPeriodStart] = useState("2025-01-01");
  const [periodEnd, setPeriodEnd] = useState("2025-12-31");
  const [countryFilter, setCountryFilter] = useState("ALL");
  const [periodFxRates, setPeriodFxRates] = useState({});
  // v0.5.9m — per-month national-return FX grid. Held here (not just in
  // CalculatePanel) so it survives step navigation like periodFxRates,
  // and is re-hydrated from the calc doc on full reload.
  const [monthlyFxRates, setMonthlyFxRates] = useState({});
  // v0.4.34 P2 — selected client id.
  const [clientId, setClientId] = useState(() => (
    typeof window !== "undefined" && localStorage.getItem("vat.selectedClientId")
  ) || null);
  // v0.6.6 — always-current mirror of clientId for the saved-calc
  // re-hydration effect below, whose async callback closes over clientId
  // from the render that scheduled it (mount) — a stale value if the
  // separate INPUTS_KEY-restore effect updates clientId before the
  // getCalc() round trip resolves.
  const clientIdRef = React.useRef(clientId);
  React.useEffect(() => { clientIdRef.current = clientId; }, [clientId]);
  // v0.4.37 V1 — clients list cached so the Step-1 chip can
  // render `{client_name}` without an extra round trip.
  const [clients, setClients] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState(null);
  const [calc, setCalc] = useState(null);
  // v0.6.6 — the calc doc itself carries no client_id (the backend never
  // persists one on CalculationResult — client_id only feeds settings/
  // registration lookups at calc time), so the auto-invalidate cascade
  // below has no field on `calc` to compare against `clientId`. Track
  // which client the CURRENT calc was actually run under locally instead,
  // so switching the client dropdown without touching runs/period still
  // drops the stale calc rather than leaving Client A's numbers reviewable
  // and freezable under Client B's selection.
  const [calcClientId, setCalcClientId] = useState(null);
  // v0.6.6 fix (regression found live) — runCalc() below correctly paired
  // calc with calcClientId, but it was the ONLY call site that did:
  // useAsyncCalc.js's onComplete handler (the actual path a real
  // calculation run completes through) called wf.setCalc(result)
  // directly, and WorkflowLayout.jsx's `?calc_id=` deep-link handler did
  // too — neither ever touched calcClientId. calcClientId stayed at its
  // OLD value (often null) while calc was freshly set, so the very next
  // tick the auto-invalidate effect below saw calcClientId !== clientId,
  // decided the calc belonged to a different client, and wiped calc back
  // to null — right after navigating to Step 4, producing a blank Review
  // screen. `commitCalc` is now the ONE path for setting a genuinely NEW
  // calc (not a same-calc field merge, which callers still use setCalc
  // for directly — see Step5Signoff.jsx / Step6FreezeExport.jsx).
  const [review, setReview] = useState(null);
  // v0.6.10 — every path that re-runs the calc must warn first if
  // acknowledgements would be stranded. The Calculate button was
  // guarded in useAsyncCalc, but runCalc() below is a SECOND way to
  // replace the calc and it was not: TraceDrawer's "Populate trace"
  // button, and the three per-row resolvers, all call it. An
  // operator lost 1,079 acknowledgements to exactly that — the
  // orphan calc had no job record because this path posts to the
  // SYNCHRONOUS /calculate endpoint, which is why it took so long
  // to find. Guarding here covers every caller at once.
  const [pendingRerun, setPendingRerun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [activeStepOverride, setActiveStepOverride] = useState(null);
  // A1-93 (5) — bump to remount CalculatePanel from context (reset/open).
  const [panelKey, setPanelKey] = useState(0);

  // v0.6 — cross-step-navigation guard for the async calc job race. Each
  // workflow step (Upload/Configure/Calculate) mounts its OWN
  // CalculatePanel, and therefore its own useAsyncCalc/useCalcJobPoll
  // instance, via react-router routes. Found live: starting a calc on
  // one step, navigating away before its poll settles, then re-running
  // from a DIFFERENT step's panel (e.g. "Save & re-run calculation" from
  // Step 2 after fixing a flagged rate) creates a second, independent
  // poll loop with no knowledge of the first. If the orphaned first
  // loop's in-flight status check resolves afterward, it has nothing to
  // compare against and fires onComplete with the stale result, which
  // clobbers `calc` with the wrong (pre-fix) figures. A ref local to one
  // hook instance can't catch this — it needs to live here, on the
  // context that spans every step. Every CalculatePanel's poll loop
  // registers its job here the instant it starts, and checks here
  // before ever applying a completion.
  const latestCalcJobIdRef = React.useRef(null);
  const registerCalcJobStart = useCallback((jobId) => {
    latestCalcJobIdRef.current = jobId;
  }, []);
  const isCurrentCalcJob = useCallback((jobId) => (
    latestCalcJobIdRef.current === jobId
  ), []);

  // v0.6 nav refactor — the active step is now driven by the URL
  // (/workflow/<slug>). setActiveStep navigates there; the override is
  // kept only as a fallback for non-routed render contexts (isolated
  // component tests / storybook). All existing setActiveStep(n) call
  // sites keep working — they now change the URL instead of a flag.
  const urlStep = parseStepFromPath(location.pathname);
  const setActiveStep = useCallback((n, navOpts) => {
    const step = Number(n);
    if (!step) return;
    setActiveStepOverride(step);
    navigate(stepPath(step), navOpts);
  }, [navigate]);

  // v0.5.9j — the calc result is re-hydrated across full page reloads
  // and hard-link navigations (several internal links are <a href> that
  // reload the SPA). We persist only the calc *id* to sessionStorage and
  // re-fetch the authoritative calc from the server on boot, restoring
  // the run/period selection so the auto-invalidate cascade keeps it.
  // A1-93 (4) — bootCalcId.js: a leftover ?calc_id= on reload/Back loses.
  const bootIdsRef = React.useRef(null);
  if (bootIdsRef.current === null) bootIdsRef.current = resolveBootCalcIds();
  const initialCalcIdRef = React.useRef(bootIdsRef.current.savedCalcId);
  // v0.6.7 fix (round 2) — captured via a useRef INITIALIZER, which runs
  // synchronously during the FIRST render, before any effect anywhere in
  // the tree (including this component's own children) can run. Reading
  // `window.location.search` live INSIDE the guarded effects below was
  // still racy: WorkflowLayout.jsx's `?calc_id=` handler itself calls
  // setActiveStep -> navigate(), which replaces the URL (stripping
  // `?calc_id=`) once it's done — and effect firing order between a
  // parent (this component) and child (WorkflowLayout) is not something
  // to depend on. By the time the guards below actually ran, the query
  // param could already be gone, silently re-enabling the exact race
  // this fix exists to prevent. Capturing it once here, at the same
  // synchronous point `initialCalcIdRef` already uses, is race-proof.
  const urlCalcIdRef = React.useRef(bootIdsRef.current.urlCalcId);
  const ignoredUrlCalcIdRef = React.useRef(bootIdsRef.current.ignoredUrlCalcId);
  // WorkflowLayout asks before opening a URL calc. A leftover stays refused
  // for this page load (not consumed: StrictMode runs effects twice in dev).
  // A1-93 r4 — `fromHistory`: reached by Back/Forward inside the app (not the
  // page's own load, which bootCalcId judges): never opened over the screen.
  const layoutMountedRef = React.useRef(false);
  const shouldOpenUrlCalc = useCallback((id, { fromHistory = false } = {}) => (
    !!id && id !== ignoredUrlCalcIdRef.current && !(fromHistory && layoutMountedRef.current)), []);
  const noteLayoutMount = useCallback(() => { layoutMountedRef.current = true; }, []);
  const bootedRef = React.useRef(false);
  // Skeleton gate: only "booting" when there is a saved calc to
  // re-hydrate from the backend, so a fresh workflow never flashes a
  // skeleton but a hard reload of an in-progress calc does.
  const [booting, setBooting] = useState(!!initialCalcIdRef.current);

  // A1-93 — remove only when a loaded calc goes away (Start over); on mount
  // it wiped the id of a link being opened (F5 mid-open reopened the old).
  const hadCalcRef = React.useRef(false);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (calc?.id) sessionStorage.setItem("vat.calcId", calc.id);
    else if (hadCalcRef.current) sessionStorage.removeItem("vat.calcId");
    hadCalcRef.current = !!calc?.id;
  }, [calc]);

  // v0.6.9 — `silent` (default true) preserves the original behaviour
  // for callers where a refresh isn't the direct consequence of an
  // operator action (initial page load, post-upload) and a transient
  // blip shouldn't interrupt them. `useRunActions.js`'s delete flow
  // passes `{ silent: false }`: found live — a delete could succeed
  // while this refresh silently failed, leaving the deleted row still
  // showing with zero explanation (no error, no "still there" hint)
  // since the failure never left this function. Same shape as the
  // runCalc()/recalcFailedMsg fix already applied elsewhere for the
  // identical "action succeeded, the follow-up silently didn't" gap.
  const refreshRuns = useCallback(async ({ silent = true } = {}) => {
    if (silent) {
      try { setRuns(await listIngestRuns()); } catch { /* ignore */ }
      return;
    }
    setRuns(await listIngestRuns());
  }, []);

  // v0.4.37 V1 — single fetch of the client list so the Step-1
  // summary chip can resolve `clientId` → `name` without a per-
  // render round trip.
  //
  // Operator check 2026-10-16: fetched only ONCE, so a client created after
  // the page opened was missing — the Step-1 chip fell back to "Default
  // client" and the workflow saw no registrations for it. Re-fetch
  // whenever the selected client is not in the list we hold.
  const clientsRef = React.useRef([]);
  React.useEffect(() => { clientsRef.current = clients; }, [clients]);
  React.useEffect(() => {
    if (!admin) return;
    const held = clientsRef.current || [];
    if (held.length && (!clientId || held.some((c) => c.id === clientId))) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listClients();
        if (!cancelled) setClients(list || []);
      } catch { /* leave clients empty */ }
    })();
    return () => { cancelled = true; };
  }, [admin, clientId]);

  const refreshSettings = useCallback(async () => {
    // A2-581 lead 2b: an OPERATOR returned here before the registrations
    // below were read, so Step 2 said "Configure seller VAT registrations"
    // and its Continue stayed disabled while Run (which reads the client
    // itself) worked. Only the global settings are ADMIN-only; the client
    // list every user reads carries the registrations.
    if (admin?.role !== "ADMIN") {
      setSettingsError("Registrations managed by an admin");
    } else {
      try {
        const s = await getSettings();
        setSettings(s);
      } catch (e) {
        setSettingsError(e?.response?.status === 403
          ? "Registrations managed by an admin"
          : "Could not load registrations");
      }
    }
    // v0.6 (found on self-review, live UI walkthrough) — registrations
    // used to come from the global /api/settings.company_vat_numbers
    // field, which the multi-tenant migration (v0.4.34+) moved to each
    // client's OWN company_vat_numbers. The Settings page's UI for
    // editing the global field was removed entirely once entities took
    // over, but this read was never repointed, so /api/settings always
    // returned an empty list here regardless of what an operator
    // configured on the entity -- "Run calculation" was permanently
    // disabled for every client. `clients` (from listClients(), which
    // already includes each client's company_vat_numbers) is the
    // correct source; find the one matching the selected clientId.
    const activeClient = (clients || []).find((c) => c.id === clientId);
    const regs = ((activeClient?.company_vat_numbers) || [])
      .map((e) => (e.country || e.country_code || "").toUpperCase())
      .filter(Boolean);
    setRegistrations(Array.from(new Set(regs)));
  }, [admin, clientId, clients]);

  // v0.6.6 — raw fetch with no state write, so runCalc() below can fetch
  // the NEW calc's review data and commit it in the SAME render as the
  // new calc itself (see comment there). Returns null on any failure
  // (missing id, 403, network) — never throws.
  const fetchReviewGrouped = useCallback(async (calcId) => {
    if (!calcId) return null;
    try {
      const { data } = await authClient.get(
        `/runs/calc/${calcId}/review-grouped`,
        { params: { page: 1, page_size: 50 } });
      return data;
    } catch {
      return null;
    }
  }, []);

  // v0.6.6 — was: on a failed fetch, silently leave `review` at whatever
  // it held before (comment claimed "leave null" but never actually did
  // it). That let a PRIOR calc's review data linger and be read against
  // a NEW calc — if the old calc happened to have zero Family-B flags,
  // the new calc's real unresolved flags never surfaced. Actually null it
  // on failure, matching the pre-first-fetch state everywhere else in the
  // app already treats `review === null` as.
  const refreshReview = useCallback(async (calcId) => {
    setReview(await fetchReviewGrouped(calcId));
  }, [fetchReviewGrouped]);

  // v0.6.6 — the ONE path for setting a genuinely NEW calc (as opposed to
  // merging a field onto the calc already in context, which stays on
  // setCalc directly — see Step5Signoff.jsx / Step6FreezeExport.jsx,
  // where run_ids/period/client are unchanged so calcClientId is still
  // valid). Keeps calc and calcClientId paired so the auto-invalidate
  // effect below never sees a stale calcClientId and wipes a calc that
  // was just legitimately set. Optionally also commits review in the
  // SAME batch when the caller already has it (runCalc does; callers
  // that don't can follow up with refreshReview separately).
  //
  // v0.6.7 fix — was `setCalcClientId(clientId)`, closing over whichever
  // `clientId` existed when THIS commitCalc instance was created. Two
  // rounds of chasing this:
  //   Round 1: switched to `clientIdRef.current` (a ref, read at CALL
  //   time instead of closed over at DEFINITION time) — genuinely fixes
  //   staleness for callers like WorkflowLayout.jsx's `?calc_id=`
  //   handler, whose `useEffect(() => {...}, [])` captures commitCalc
  //   once at mount and would otherwise never see clientId's later
  //   updates.
  //   Round 2 (the actual remaining bug): that handler ALSO calls
  //   `setClientId(c.client_id)` immediately before commitCalc(c), in
  //   the SAME synchronous block, when the calc doc happens to carry a
  //   client_id. A ref update only happens via this component's own
  //   `useEffect(() => { clientIdRef.current = clientId }, [clientId])`
  //   — which, like the setClientId call itself, only takes effect on
  //   the NEXT render. So at the exact moment commitCalc runs,
  //   clientIdRef.current is STILL the value from BEFORE that
  //   setClientId call — reproduced live in a completely clean browser
  //   session (no stale cache of any kind): the deep-linked calc's own
  //   client_id was "default", clientIdRef.current was still null at
  //   call time, calcClientId got set to null, and the instant clientId
  //   itself finished updating to "default" a render later, the
  //   auto-invalidate effect below saw calcClientId (null) !== clientId
  //   ("default") and wiped the just-loaded, genuinely-current calc
  //   back to null.
  // Fix: accept the correct client id EXPLICITLY when the caller
  // already knows it synchronously (WorkflowLayout.jsx has `c.client_id`
  // sitting right there — no state or ref round-trip needed at all),
  // falling back to the ref for every other caller unchanged.
  const commitCalc = useCallback((result, reviewData, explicitClientId) => {
    setCalc(result);
    // A2-613 review (finding 4): the calc keeps the rates the server added
    // (ECB day rates, a return's own BMF/ECB rates), so the form takes the
    // calc's grid, as openCalc does — else the fresh calc reads as stale
    // ("Exchange rates have changed") until it is reopened.
    if (result?.monthly_fx_rates && typeof result.monthly_fx_rates === "object") {
      setMonthlyFxRates(result.monthly_fx_rates);
    }
    if (reviewData !== undefined) setReview(reviewData);
    setCalcClientId(
      explicitClientId !== undefined ? explicitClientId : clientIdRef.current,
    );
  }, []);

  // A1-93 — the ONE path for opening an EXISTING calc (reload, link,
  // History, "View current calculation"): every input it was run with goes
  // back on the form (files, period, client, FX, country filter), review
  // first so calc + review land together. FX used to be seeded only into an
  // EMPTY grid, so an in-app jump kept the previous calc's rates. r4:
  // `shouldCommit` lets a caller that was left mid-open drop it (false).
  const openCalc = useCallback(async (c, { shouldCommit } = {}) => {
    const reviewData = await fetchReviewGrouped(c.id);
    if (shouldCommit && !shouldCommit()) return false;
    setSelectedRunIds(c.run_ids || []);
    const ps = String(c.period_start || "").slice(0, 10);
    const pe = String(c.period_end || "").slice(0, 10);
    if (ps) setPeriodStart(ps);
    if (pe) setPeriodEnd(pe);
    setPeriodFxRates(normalizePeriodFxRates(c.period_fx_rates, c.regime));
    setMonthlyFxRates(c.monthly_fx_rates || {});
    setCountryFilter(String(c.country_filter || "ALL").toUpperCase());
    if (c.client_id) {
      setClientId(c.client_id);
      commitCalc(c, reviewData, c.client_id);
    } else {
      // A calc that does not record its client: never adopt the dropdown's
      // (that is how X's figures came to show under Y). Unknown -> the
      // client is not compared; the selector is locked while it is loaded.
      setCalc(c); setReview(reviewData); setCalcClientId(undefined);
    }
    setPanelKey((k) => k + 1);
    return true;
  }, [fetchReviewGrouped, commitCalc]);

  const runCalcRef = React.useRef(null);

  const runCalc = useCallback(async (opts) => {
    // `force: true` is the second pass, after the operator confirmed.
    const acked = (review?.groups || [])
      .reduce((sum, g) => sum + (g.acknowledged_count || 0), 0);
    if (!opts?.force && calc?.id && acked > 0) {
      return new Promise((resolve) => {
        setPendingRerun({
          ackedCount: acked,
          onProceed: () => {
            setPendingRerun(null);
            resolve(runCalcRef.current?.({ force: true }));
          },
          onCancel: () => { setPendingRerun(null); resolve(false); },
        });
      });
    }
    setBusy(true); setError(null);
    try {
      // v0.6 — payload built via the shared buildCalcPayload() helper
      // (frontend/src/lib/calcPayload.js) instead of spelling out the
      // field list here by hand. This also fixes a real bug found while
      // extracting it: this call never sent `client_id`, so a recalc
      // triggered from a review-queue resolver (VAT number amend,
      // refund-channel resolve, service-country resolve) on a non-default
      // client silently rehydrated the backend's settings_cache from
      // whichever client is marked default instead of the one actually
      // being worked on.
      const res = await calcApi(buildCalcPayload({
        registrations, runIds: selectedRunIds,
        periodStart, periodEnd, countryFilter,
        periodFxRates, monthlyFxRates, clientId,
      }));
      // v0.6.6 — fetch the new calc's review BEFORE committing either
      // piece of state, then set calc/review/calcClientId together.
      // Previously `setCalc(res)` landed first and `await refreshReview`
      // committed on its own later tick — any gate read from context in
      // that window (e.g. Step5Signoff's signoffDisabled) paired the NEW
      // calc with the OLD calc's review data.
      const reviewData = await fetchReviewGrouped(res?.id);
      commitCalc(res, reviewData);
      // v0.6.6 — return success so callers that treat a recalc as part of
      // their OWN action's outcome (per-row resolvers: assign channel /
      // amend VAT number / resolve service country, each of which shows
      // "success — re-running" and only then calls this) can tell the
      // recalculation itself failed. runCalc() still swallows its own
      // errors into `error` state (existing callers that don't check the
      // return value are unaffected) — this only ADDS a signal.
      //
      // v0.6.7 — return the NEW calc's id (still truthy, so every
      // existing `if (!ok)` failure check keeps working unchanged)
      // instead of a bare `true`. Per-row resolvers forward this to
      // their own `onResolved(id)` so ReviewGroupCard's callbacks can
      // refetch against the id that's ACTUALLY current, rather than the
      // `calcId` prop closed over at render time — which is already
      // stale by the time this recalc finishes, since commitCalc just
      // replaced it with a brand new uuid.
      return res?.id || true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, [registrations, selectedRunIds, periodStart, periodEnd, countryFilter, periodFxRates, monthlyFxRates, clientId, fetchReviewGrouped, commitCalc, review, calc?.id]);
  React.useEffect(() => { runCalcRef.current = runCalc; }, [runCalc]);

  // v0.4.38 — operator can wipe the in-progress workflow and return
  // to Step 1 (Upload). Clears local state only: ingest runs stay in
  // Mongo, client selection is preserved (the operator just chose
  // it). Calc is dropped, review queue is dropped, period resets to
  // current-year defaults, country filter resets to ALL.
  // A1-93 (5) — also rebuilds CalculatePanel (panelKey): on step 1 it kept
  // its files ticked, a run sent them and the result was dropped at once.
  // A calc job still polling is disowned so its result cannot land.
  const clearWorkflow = useCallback(() => {
    setSelectedRunIds([]);
    setCalc(null);
    setCalcClientId(null);
    setReview(null);
    setPeriodStart("2025-01-01");
    setPeriodEnd("2025-12-31");
    setCountryFilter("ALL");
    setPeriodFxRates({});
    setMonthlyFxRates({});
    setError(null);
    latestCalcJobIdRef.current = null;
    setPanelKey((k) => k + 1);
    if (typeof window !== "undefined") {
      try { localStorage.removeItem("vat.workflowInputs"); } catch { /* ignore */ }
    }
  }, []);
  const resetWorkflow = useCallback(() => {
    clearWorkflow();
    setActiveStepOverride(1);
    navigate(stepPath(1));
  }, [navigate, clearWorkflow]);

  // A1-93 (9) — signing out kept the calc, selections and client here (this
  // sits above the router): the next person landed on them. Clear all.
  const hadAdminRef = React.useRef(false);
  React.useEffect(() => {
    if (admin) { hadAdminRef.current = true; return; }
    if (!hadAdminRef.current) return;
    hadAdminRef.current = false;
    clearWorkflow();
    setClientId(null);
    initialCalcIdRef.current = null;
    urlCalcIdRef.current = null;
    try {
      sessionStorage.removeItem("vat.calcId");
      localStorage.removeItem("vat.selectedClientId");
    } catch { /* ignore */ }
  }, [admin, clearWorkflow]);

  // A1-93 (6) — the auto-invalidate cascade used to DROP the calc here
  // whenever files/period/client moved, silently: the "inputs have
  // changed — re-run required" message could never show, Steps 4-6 said
  // "No active calculation" for a reason that was not true, and the
  // review-notes warning before a re-run never fired (there was no calc
  // left to protect). The calc now stays loaded and deriveWorkflowState
  // marks it STALE with the true reasons (calcInputs.js — which also
  // covers FX and the country filter, never compared before). The
  // client comparison (v0.6.6, calcClientId) moved there too.

  const sellerCountry = registrations[0] || "";
  const state = useMemo(() => deriveWorkflowState({
    selectedRunIds, periodStart, periodEnd, registrations, sellerCountry,
    calc, review, clientId, calcClientId, countryFilter, periodFxRates, monthlyFxRates,
  }), [selectedRunIds, periodStart, periodEnd, registrations, sellerCountry, calc, review,
       clientId, calcClientId, countryFilter, periodFxRates, monthlyFxRates]);

  const value = useMemo(() => ({
    runs, selectedRunIds, setSelectedRunIds,
    periodStart, setPeriodStart, periodEnd, setPeriodEnd,
    countryFilter, setCountryFilter,
    periodFxRates, setPeriodFxRates,
    monthlyFxRates, setMonthlyFxRates,
    clientId, setClientId,
    clients,
    registrations, settings, settingsError,
    calc, setCalc, commitCalc, openCalc, review, busy, error, panelKey, shouldOpenUrlCalc, noteLayoutMount,
    state, activeStep: urlStep ?? (activeStepOverride ?? state.activeStep),
    setActiveStep,
    booting,
    refreshRuns, refreshSettings, refreshReview, runCalc,
    resetWorkflow,
    registerCalcJobStart, isCurrentCalcJob,
  }), [runs, selectedRunIds, periodStart, periodEnd, countryFilter,
       periodFxRates, monthlyFxRates,
       registrations, settings, settingsError, calc, review, busy, error, panelKey,
       state, urlStep, activeStepOverride, setActiveStep, booting,
       refreshRuns, refreshSettings, refreshReview, runCalc, commitCalc, openCalc,
       resetWorkflow, registerCalcJobStart, isCurrentCalcJob, shouldOpenUrlCalc, clientId, clients]);

  React.useEffect(() => { if (admin) refreshRuns(); }, [admin, refreshRuns]);
  React.useEffect(() => { if (admin) refreshSettings(); }, [admin, refreshSettings]);

  useWorkflowRestore({
    admin, bootedRef, initialCalcIdRef, urlCalcIdRef, setBooting, openCalc,
    runs, selectedRunIds, setSelectedRunIds, periodStart, setPeriodStart,
    periodEnd, setPeriodEnd, periodFxRates, setPeriodFxRates, clientId, setClientId, setPanelKey,
  });
  useCalcStatusRefresh(calc?.id, setCalc);

  return (
    <WorkflowCtx.Provider value={value}>
      {children}
      <RerunWarningDialog pending={pendingRerun} />
    </WorkflowCtx.Provider>
  );
};

export const useWorkflowOptional = () => {
  // Returns the workflow context if present, else null. Safe to call
  // unconditionally — does NOT throw outside a provider. Used by
  // components that may render in legacy contexts (e.g. Storybook
  // snapshots, isolated test renders).
  return useContext(WorkflowCtx);
};

export const useWorkflow = () => {
  const v = useContext(WorkflowCtx);
  if (!v) throw new Error("useWorkflow must be used inside WorkflowProvider");
  return v;
};
